import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2, SkipForward, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [schema, setSchema] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [value, setValue] = useState("");
  const [custom, setCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    api.get("/onboarding").then(({ data }) => {
      setSchema(data);
      setAnswers(data.answers || {});
      const resume = data.completed ? 0 : Math.min(data.current_step || 0, data.total_steps - 1);
      setStep(resume);
    }).catch(() => {});
  }, []);

  const questions = schema?.questions || [];
  const q = questions[step];
  const total = schema?.total_steps || 10;

  useEffect(() => {
    if (!q) return;
    const existing = answers[q.key] ?? (q.key === "first_name" ? (user?.name || "").split(" ")[0] : "");
    setValue(existing || "");
    setCustom(existing ? !(q.options || []).includes(existing) : false);
  }, [step, q]); // eslint-disable-line

  const persist = useCallback(async (key, val, nextStep) => {
    setAnswers((a) => ({ ...a, [key]: val }));
    try { await api.put("/onboarding/answer", { key, value: val, current_step: nextStep }); }
    catch (e) { /* autosave best-effort */ }
  }, []);

  const canAdvance = q && (q.optional || String(value).trim().length > 0);

  const next = async () => {
    if (!q) return;
    if (!q.optional && !String(value).trim()) { toast.error("Please answer to continue"); return; }
    setSaving(true);
    await persist(q.key, String(value).trim(), step + 1);
    setSaving(false);
    if (step + 1 >= total) return finish();
    setStep(step + 1);
  };

  const back = () => step > 0 && setStep(step - 1);

  const skip = async () => {
    if (!q?.optional) return;
    await persist(q.key, "", step + 1);
    if (step + 1 >= total) return finish();
    setStep(step + 1);
  };

  const skipAll = async () => {
    try { await api.post("/onboarding/skip"); } catch (e) { /* ignore */ }
    localStorage.removeItem("fv_onboard");
    navigate("/dashboard");
  };

  const finish = async () => {
    setFinishing(true);
    try { await api.post("/onboarding/complete"); localStorage.removeItem("fv_onboard");
      toast.success("You're all set — welcome aboard!"); navigate("/dashboard"); }
    catch (e) { toast.error("Could not finish onboarding"); setFinishing(false); }
  };

  if (!schema) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div>;

  const pct = Math.round((step / total) * 100);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white flex flex-col" data-testid="onboarding-page">
      {/* Header + progress */}
      <div className="border-b border-white/8 px-5 sm:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg fv-gold-gradient flex items-center justify-center">
            <span className="font-heading text-[#0A0A0B] font-bold">F</span>
          </div>
          <span className="font-heading text-lg">Fairview <span className="text-[#D4AF37]">Systems</span></span>
        </div>
        <button onClick={skipAll} data-testid="onboarding-skip-all" className="text-sm text-[#71717A] hover:text-white transition-colors">
          Skip for now
        </button>
      </div>
      <div className="h-1.5 bg-white/5">
        <motion.div className="h-full fv-gold-gradient" animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} />
      </div>

      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-xl">
          <div className="flex items-center justify-between mb-6 text-xs">
            <span className="text-[#D4AF37] tracking-[0.2em] uppercase flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Let's get you set up</span>
            <span className="text-[#71717A]" data-testid="onboarding-progress-label">Step {step + 1} of {total} · {pct}%</span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={step}
              initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.25 }}>
              <h1 className="font-heading text-3xl sm:text-4xl mb-2" data-testid="onboarding-question">{q.question}</h1>
              {q.help && <p className="text-[#A1A1AA] text-sm mb-6">{q.help}</p>}
              {!q.help && <div className="mb-6" />}

              {/* Options */}
              {(q.type === "select" || q.type === "select_or_custom") && !custom && (
                <div className="grid sm:grid-cols-2 gap-2.5 mb-4" data-testid="onboarding-options">
                  {q.options.map((opt) => (
                    <button key={opt} data-testid={`onboarding-option-${opt.slice(0, 12).replace(/\s/g, "-").toLowerCase()}`}
                      onClick={() => setValue(opt)}
                      className={`text-left px-4 py-3 rounded-xl border text-sm transition-colors ${value === opt ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/10 text-[#D4D4D8] hover:border-white/25"}`}>
                      {value === opt && <Check className="w-4 h-4 text-[#D4AF37] inline mr-2" />}{opt}
                    </button>
                  ))}
                </div>
              )}
              {q.type === "select_or_custom" && (
                <button onClick={() => { setCustom(!custom); setValue(""); }} data-testid="onboarding-custom-toggle"
                  className="text-xs text-[#D4AF37] hover:underline mb-4">
                  {custom ? "← Choose from list" : "Enter my own answer"}
                </button>
              )}

              {/* Text / custom input */}
              {(q.type === "text" || ((q.type === "select_or_custom") && custom)) && (
                <Input autoFocus value={value} onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && canAdvance && next()}
                  placeholder={q.placeholder || "Type your answer"} data-testid="onboarding-input"
                  className="bg-[#121214] border-white/10 text-white h-12 text-base mb-4" />
              )}

              <div className="flex items-center justify-between mt-6">
                <button onClick={back} disabled={step === 0} data-testid="onboarding-back"
                  className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white disabled:opacity-30 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <div className="flex items-center gap-3">
                  {q.optional && (
                    <button onClick={skip} data-testid="onboarding-skip"
                      className="flex items-center gap-1.5 text-sm text-[#71717A] hover:text-white transition-colors">
                      <SkipForward className="w-4 h-4" /> Skip
                    </button>
                  )}
                  <Button onClick={next} disabled={saving || finishing || !canAdvance} data-testid="onboarding-next"
                    className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-7 h-11">
                    {finishing ? "Finishing…" : step + 1 >= total ? "Finish" : "Continue"}
                    {!finishing && <ArrowRight className="w-4 h-4 ml-1.5" />}
                  </Button>
                </div>
              </div>
              <p className="text-[10px] text-[#52525B] mt-4 flex items-center gap-1.5">
                <Check className="w-3 h-3 text-emerald-400" /> Auto-saved — you can leave and resume anytime.
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
